import { Amplify } from 'aws-amplify';
import {
  AuthError,
  confirmResetPassword as amplifyConfirmResetPassword,
  confirmSignIn as amplifyConfirmSignIn,
  confirmSignUp as amplifyConfirmSignUp,
  FetchAuthSessionOptions,
  resendSignUpCode as amplifyResendSignUpCode,
  resetPassword as amplifyResetPassword,
  sendUserAttributeVerificationCode as amplifySendUserAttributeVerificationCode,
  signIn as amplifySignIn,
  SignInOutput,
  signUp as amplifySignUp,
  updateUserAttribute as amplifyUpdateUserAttribute,
  updateUserAttributes as amplifyUpdateUserAttributes,
} from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { uploadData, UploadDataWithPathOutput } from 'aws-amplify/storage';
import { MutableRefObject } from 'react';

export function configureAmplify() {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
        identityPoolId: process.env.REACT_APP_COGNITO_IDENTITY_POOL_ID,
        userPoolClientId: process.env.REACT_APP_COGNITO_APP_CLIENT_ID,
        ...(process.env.REACT_APP_COGNITO_ENDPOINT && {
          userPoolEndpoint: process.env.REACT_APP_COGNITO_ENDPOINT,
        }),
      },
    },
    Storage: {
      S3: {
        bucket: process.env.REACT_APP_S3_BUCKET_NAME,
        region: process.env.REACT_APP_COGNITO_REGION,
      },
    },
  });
  // Token refreshes send the domain too, as v4's configured default did
  cognitoUserPoolsTokenProvider.setClientMetadataProvider(() => Promise.resolve(requiredClientMetadata));
}

// The Cognito email sender picks the white-label template from the domain, so every Auth call must send it
const requiredClientMetadata = { domain: window.location.hostname };

type ClientMetadataInput = { options?: { clientMetadata?: Record<string, string> } };

// Amplify v6 has no default metadata: wraps an Auth call so it always sends the domain, merged with the call's own
const withRequiredClientMetadata = <Input extends ClientMetadataInput, Output>(
  authCall: (input: Input) => Promise<Output>,
) => {
  const authCallWithMetadata = (input: Input) =>
    authCall({
      ...input,
      options: { ...input.options, clientMetadata: { ...requiredClientMetadata, ...input.options?.clientMetadata } },
    });
  return authCallWithMetadata;
};

// Use these instead of the aws-amplify/auth ones
export const confirmSignIn = withRequiredClientMetadata(amplifyConfirmSignIn);
export const signUp = withRequiredClientMetadata(amplifySignUp);
export const confirmSignUp = withRequiredClientMetadata(amplifyConfirmSignUp);
export const resendSignUpCode = withRequiredClientMetadata(amplifyResendSignUpCode);
export const resetPassword = withRequiredClientMetadata(amplifyResetPassword);
export const confirmResetPassword = withRequiredClientMetadata(amplifyConfirmResetPassword);
export const updateUserAttributes = withRequiredClientMetadata(amplifyUpdateUserAttributes);
export const updateUserAttribute = withRequiredClientMetadata(amplifyUpdateUserAttribute);
export const sendUserAttributeVerificationCode = withRequiredClientMetadata(amplifySendUserAttributeVerificationCode);
const signIn = withRequiredClientMetadata(amplifySignIn);

// Thrown when nobody is signed in, so callers can tell a signed-out user from a real failure
export class NoCurrentUserError extends Error {
  constructor() {
    super('No current user');
    this.name = 'NoCurrentUserError';
  }
}

type SignInStep = SignInOutput['nextStep']['signInStep'];

// Derived from Amplify's union, so a renamed step fails to compile
const CONFIRM_SIGN_UP_STEP: Extract<SignInStep, 'CONFIRM_SIGN_UP'> = 'CONFIRM_SIGN_UP';
const RESET_PASSWORD_STEP: Extract<SignInStep, 'RESET_PASSWORD'> = 'RESET_PASSWORD';

// v6 reports these as sign-in steps where Cognito (and v4) threw; throw them again so every page handles them the same
const signInStepErrors: Partial<Record<SignInStep, { name: string; message: string }>> = {
  [CONFIRM_SIGN_UP_STEP]: { name: 'UserNotConfirmedException', message: 'User is not confirmed.' },
  [RESET_PASSWORD_STEP]: { name: 'PasswordResetRequiredException', message: 'Password reset required for the user' },
};

const alreadySignedIn: SignInOutput = { isSignedIn: true, nextStep: { signInStep: 'DONE' } };

// Sign-in runs SRP, then the emailed-code custom challenge driven by the Cognito auth triggers
export const signInWithCustomChallenge = async (
  username: string,
  password: string,
  clientMetadata?: Record<string, string>,
) => {
  const result = await signInUnlessSignedIn({
    username,
    password,
    options: { authFlowType: 'CUSTOM_WITH_SRP', clientMetadata },
  });
  const stepError = signInStepErrors[result.nextStep.signInStep];
  if (stepError) throw new AuthError(stepError);
  return result;
};

// v6 refuses to sign in while a valid session is stored, e.g. one another tab just started. Report it as signed in, so
// the page picks that session up, instead of signing out and revoking it for every tab.
const signInUnlessSignedIn = async (input: Parameters<typeof signIn>[0]) => {
  try {
    return await signIn(input);
  } catch (error) {
    if (error instanceof AuthError && error.name === 'UserAlreadyAuthenticatedException') return alreadySignedIn;
    throw error;
  }
};

// Refreshes expired tokens, and throws NoCurrentUserError when nobody is signed in. Reads user pool tokens only:
// fetchAuthSession would also fetch identity pool credentials, which only uploads need.
export const getSessionTokens = async (options?: FetchAuthSessionOptions) => {
  const tokens = await cognitoUserPoolsTokenProvider.getTokens(options);
  if (!tokens?.idToken) throw new NoCurrentUserError();
  return {
    idToken: tokens.idToken.toString(),
    idTokenPayload: tokens.idToken.payload,
    accessToken: tokens.accessToken.toString(),
  };
};

export interface UploadFileConfig {
  bucket?: string;
  url?: string;
  path?: string;
}

export interface UploadProgress {
  loaded: number;
  total: number;
}

export type UploadOperation = UploadDataWithPathOutput;

export interface UploadFileOptions {
  onProgress?: (progress: UploadProgress) => void;
  // Receives the running upload, so it can be cancelled with cancelUpload
  uploadOperationRef?: MutableRefObject<UploadOperation>;
  config?: UploadFileConfig;
  isPrivate?: boolean;
}

export const uploadFile = async (file: File, options?: UploadFileOptions) => {
  const fileName = `${options?.config?.path || ''}${crypto.randomUUID()}`;
  const uploadOperation = uploadData({
    // Same object keys as the v4 public/private access levels; v6 asserts the identity id for the private one
    path: options?.isPrivate ? ({ identityId }) => `private/${identityId}/${fileName}` : `public/${fileName}`,
    data: file,
    options: {
      contentType: file.type,
      bucket: {
        bucketName: options?.config?.bucket || process.env.REACT_APP_S3_BUCKET_NAME,
        region: process.env.REACT_APP_COGNITO_REGION,
      },
      onProgress: ({ transferredBytes, totalBytes }) =>
        options?.onProgress?.({ loaded: transferredBytes, total: totalBytes ?? file.size }),
    },
  });
  // Stored before any await, so a cancel right after starting reaches this upload
  if (options?.uploadOperationRef) options.uploadOperationRef.current = uploadOperation;

  const { path } = await uploadOperation.result;
  // The base URLs already end with the access-level folder
  return `${options?.config?.url || process.env.REACT_APP_S3_URI}${path.replace(/^(public|private)\//, '')}`;
};

// Cancels the upload uploadFile stored in the ref, if any; its result then rejects
export const cancelUpload = (uploadOperationRef: MutableRefObject<UploadOperation>) => {
  uploadOperationRef.current?.cancel();
  uploadOperationRef.current = undefined;
};
